import { IdentityUtils } from "abreact/core/identity/IdentityUtils";
import { PageStateTools } from "abreact/core/pagestate/PageStateTools";
/**
 * IMPORTANT:
 * See The PageState User's Guide in Confluence for full implementation
 * details and guidance:
 *
 * https://abinitio.atlassian.net/wiki/x/AYDIT
 *
 * or (alternate link)
 *
 * https://abinitio.atlassian.net/wiki/spaces/MHTEAM/pages/1288208385/PageState+User+s+Guide
 *
 */
export let HasPageState;
(function (_HasPageState) {
  function Observer(target) {
    return PageStateTools.hasPageStateInternalHOC(target, true);
  }
  _HasPageState.Observer = Observer;
  function NonObserver(target) {
    return PageStateTools.hasPageStateInternalHOC(target, false);
  }
  _HasPageState.NonObserver = NonObserver;
})(HasPageState || (HasPageState = {}));
/**
 * Method that must be called in the constructor by any class component that has page state (observable or not)
 * constructor()
 * {
 *   do stuff
 *   enablePageState(this);
 * }
 */
export function enablePageState(target) {
  PageStateTools.initializeComponentFromConstructor(target);
}

/**
 * Annotation that marks a field as being "reactive" page state (either mobx observable or forceUpdate reactive,
 * see class annotation above). Values set before the first render are default values that will not be persisted
 * as page state. All other values set will be remembered and restored if the page is reloaded from history.
 */
export function PageState(target, key, propertyDescriptor) {
  PageStateTools.addPageStateInfo(target, {
    field: key,
    propertyDescriptor: propertyDescriptor,
    isSilent: false
  });
}

/**
 * Annotation that marks a field as being "NON reactive" page state. This is used in cases where some value
 * needs to be remembered when the page is reloaded but a re-render each time it changes is not desired.
 *
 * Values set before the first render are default values that will not be persisted
 * as page state. All other values set will be remembered and restored if the page is reloaded from history.
 */
export function PageStateSilent(target, key, propertyDescriptor) {
  PageStateTools.addPageStateInfo(target, {
    field: key,
    propertyDescriptor: propertyDescriptor,
    isSilent: true
  });
}

/**
 * Used to obtain the ComponentIdentitySupport object needed to calle renderChildrenWithIdentity in a functional component.
 * This is a hook, so be sure to 1) call it on every render cycle and 2) only call it once per render cycle
 *
 * Example:
 *
 * export const MyFunc: React.FC<MyFuncProps> = (props: MyFuncProps) =>
 * {
 *   const identitySupport = useComponentIdentitySupport(props, "MyFunc", () => this.props.compositeId)
 *   ...renderChildrenWithIdentity documentation below
 *
 *   NOTE: idenitySupport is also a pageStateSupport, so you do not need to get a separate identitySupport if you
 *   already have a pageStateSupport
 *
 * @param props
 * @param componentName see usePageStateSupport for doc
 * @param keyFunc see usePageStateSupport for doc
 */
export function useComponentIdentitySupport(props, componentName, keyFunc) {
  return IdentityUtils.useComponentIdentitySupportInternal(props, componentName, keyFunc);
}

/**
 * Used to obtain the ComponentPageStateSupport object needed to use page state within a functional component. This is
 * a hook, so be sure to 1) call it on every render cycle and 2) only call it once per render cycle
 *
 * Example:
 *
 * export const MyFunc: React.FC<MyFuncProps> = (props: MyFuncProps) =>
 * {
 *   const pageStateSupport = usePageStateSupport(props, "MyFunc", () => this.props.compositeId)
 *   ...see usePageState documentation below
 *
 * @param props the props passed into the function, untyped (the type doesn't matter here)
 * @param componentName since this code is inside the function, the ts identifier from the constant is not available, so the
 *                   component name must be passed in specifically as a string
 * @param keyFunc if there is a property that works well as a key (like compositeId),
 *                 then use this function to pass that value along. By default, the system will look for "id" or "dataTestId" to use as differentiators, but
 *                 the react key is guarded by react and not available
 */
export function usePageStateSupport(props, componentName, keyFunc) {
  return PageStateTools.usePageStateSupportInternal(props, componentName, keyFunc);
}

/**
 * Same API as React.useState except with re-instated values when re-rendered via browser history
 *
 * @param pageStateSupport see usePageStateSupport above
 * @param initialState
 */
export function usePageState(pageStateSupport, initialState) {
  return PageStateTools.usePageStateInternal(false, pageStateSupport, initialState);
}
/**
 * Same API as usePageState except that incoking the setter does not cause a render cycle
 *
 * @param pageStateSupport see usePageStateSupport above
 * @param initialState
 */
export function useSilentPageState(pageStateSupport, initialState) {
  return PageStateTools.usePageStateInternal(true, pageStateSupport, initialState);
}

/**
 * Functional components that are effectively containers (e.g. AbHBox or any new component that is added to server as a
 * container of other components) should use this method to ensure that their children have unambiguous identity for their
 * page state values.
 *
 * const MyFuncInternal = (props) {
 *   ...
 *   const identitySupport = useComponentIdentitySupport(props, "MyFunc", () => this.props.compositeId)
 *   {
 *     renderChildrenWithIdentity(identitySupport, props)
 *   }
 * }
 *
 * NOTE: if this function uses page state and hase a PageStateSupportContext, then that can used as the identitySupport as well.
 * there is no need to also call useComponentIdentitySupport
 *
 * @param parentIdentityContext
 * @param props
 */
export function renderChildrenWithIdentity(componentIdentitySupport, props) {
  return IdentityUtils.renderChildrenWithIdentityInternal(componentIdentitySupport?.identity, props.children);
}

/**
 * Used to render contained content that is not an actual react child, like in the case of a render prop, and have
 * the current component included in the identity of the contained element.
 *
 * Example:
 *
 *  renderContentWithIdentity(identitySupport, this.props.renderContent());
 *
 * @param componentIdentitySupport
 * @param content
 */
export function renderContentWithIdentity(componentIdentitySupport, content) {
  return IdentityUtils.renderChildrenWithIdentityInternal(componentIdentitySupport?.identity, content);
}

/**
 * Class components that are effectively containers (e.g. AbPanel or any new component that is added to server as a
 * container of other components) should use this method to ensure that their children have unambiguous identity for their
 * page state values.
 *
 *   render()
 *   {
 *     ... other code
 *     {
 *      renderChildrenWithIdentityForClassComponent(this);
 *     }
 *   }
 *
 *
 * @param parentIdentityContext
 * @param props
 */
export function renderChildrenWithIdentityForClassComponent(parentComponent) {
  return IdentityUtils.renderContentWithIdentityForClassComponentInternal(parentComponent, parentComponent.props.children);
}

/**
 * Used to render contained content that is not an actual react child for a class component, like in the
 * case of a render prop, and have the current component included in the identity of the contained element.
 *
 * Example:
 *
 *  render()
 *   {
 *     ... other code
 *     {
 *      renderContentWithIdentityForClassComponent(this, this.props.renderContent());
 *     }
 *   }
 *
 * @param componentIdentitySupport
 * @param content
 */
export function renderContentWithIdentityForClassComponent(parentComponent, content) {
  return IdentityUtils.renderContentWithIdentityForClassComponentInternal(parentComponent, content);
}

/**
 * Rarely used, but this will result in a container component that is not structural, such as AbErrorBoundary (or perhaps
 * others that maybe render conditionally via a test or something) to be omitted from the qualifiedId computation for
 * children it contains.
 *
 * Can be applied to a Class, const holding a function, or a function directly
 *
 * @param target
  */
export function OmitFromIdentity(target) {
  IdentityUtils.omitFromIdentityInternal(target);
  return target;
}
//# sourceMappingURL=PageStateApi.js.map
